import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { UniformGalleryService } from '../instances/uniform-gallery';
import { UniformGalleryConverter } from '@/converters';

export const useUniformGallery = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_UNIFORM_GALLERY', params],
    async () => {
      const [err, data] = await UniformGalleryService.getUniformGalleries(params);
      if (err) {
        throw err;
      }
      return {
        data: UniformGalleryConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useUniformGalleryById = (id: string) => {
  const query = useQuery(
    ['GET_UNIFORM_GALLERY_BY_ID', id],
    async () => {
      const [err, data] = await UniformGalleryService.getUniformGalleryById(id);
      if (err) {
        throw err;
      }
      return UniformGalleryConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostUniformGalleryMutation = () => {
  return useMutation({
    mutationKey: ['POST_UNIFORM_GALLERY'],
    mutationFn: async (payload: any) => {
      const [err, data] = await UniformGalleryService.postUniformGallery(payload);
      if (err) {
        throw err;
      }
      return UniformGalleryConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutUniformGalleryMutation = () => {
  return useMutation({
    mutationKey: ['PUT_UNIFORM_GALLERY'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await UniformGalleryService.putUniformGallery(id, payload);
      if (err) {
        throw err;
      }
      return UniformGalleryConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteUniformGalleryMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_UNIFORM_GALLERY'],
    mutationFn: async (id: string) => {
      const [err] = await UniformGalleryService.deleteUniformGallery(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetUniformGalleryById = () => {
  return useMutation({
    mutationFn: async (uuid: string) => {
      const [err, data] = await UniformGalleryService.getUniformGalleryById(uuid);
      if (err) {
        throw err;
      }
      return UniformGalleryConverter.toDomainFromByIdResponse(data);
    },
  });
};
